/**
 * 共享 API 客户端：统一 fetch 包装、错误类型与 401 全局通知。
 *
 * 三个 app 的 api.ts 都基于这里的 `request`，会话失效（401）时
 * 统一通知监听者——home 外壳（登录门）用它切回登录视图。
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** 把捕获的异常转成用户可读文案（非 Error 一律「操作失败」）。 */
export function describeError(e: unknown): string {
  return e instanceof Error ? e.message : '操作失败';
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** 注册 401 监听（会话失效时触发）；返回取消注册函数。 */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

function notifyUnauthorized() {
  for (const listener of unauthorizedListeners) listener();
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(path, {
    method,
    headers:
      body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (response.status === 204) {
    return undefined as T;
  }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401) notifyUnauthorized();
    const message = (data as { error?: string } | null)?.error;
    throw new ApiError(
      response.status,
      message ?? `请求失败（${response.status}）`,
    );
  }
  return data as T;
}

/** 认证状态（GET /api/auth/status）。 */
export interface AuthStatus {
  enabled: boolean;
  authenticated: boolean;
}

/** 查询认证状态；接口不可达时返回 null（按无门处理，由业务请求报错）。 */
export async function fetchAuthStatus(): Promise<AuthStatus | null> {
  try {
    const response = await fetch('/api/auth/status');
    if (!response.ok) return null;
    return (await response.json()) as AuthStatus;
  } catch {
    return null;
  }
}

/** 登出：成功（含本就无会话的 401）都视为已登出。 */
export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
}
